/* ==========================================================
   Zewye90 — gallery.js
   Story-style coverflow for the customer gallery.

   Built on a native scroll container with snap points. Each card is
   transformed on its own (tilt + scale), so there is never one
   oversized GPU layer — the thing that broke the scrolling wall on
   iPhone. The ambient glow is a single static layer whose image is
   crossfaded, never animated per frame.
   ========================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.dataset.galReady) return;
    root.dataset.galReady = '1';

    var track = root.querySelector('[data-g-track]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-g-item]'));
    if (!track || !items.length) return;

    root.classList.add('gal-js');

    var counter = root.querySelector('[data-g-count]');
    var prevBtn = root.querySelector('[data-g-prev]');
    var nextBtn = root.querySelector('[data-g-next]');
    var amb = Array.prototype.slice.call(root.querySelectorAll('[data-g-amb]'));
    var ambIdx = 0, ambTok = 0;

    var tilt = REDUCED ? 0 : (parseFloat(root.dataset.tilt) || 0);
    var auto = !REDUCED && root.dataset.auto === '1' && items.length > 1;
    if (auto) root.classList.add('is-auto');

    var active = -1;
    var ticking = false;
    var hold = { hover: false, touch: false, offscreen: false };
    var touchTimer = null;

    function syncPause() {
      var paused = hold.hover || hold.touch || hold.offscreen || document.hidden;
      root.classList.toggle('is-paused', paused);
    }

    function gap() { return parseFloat(getComputedStyle(track).columnGap) || 0; }

    /* offsetLeft ignores transforms, so positions stay stable while the
       cards are tilting and scaling */
    function update() {
      ticking = false;
      var mid = track.clientWidth / 2;
      var g = gap();
      var best = 0, bestD = Infinity;

      for (var i = 0; i < items.length; i++) {
        var el = items[i];
        var off = (el.offsetLeft + el.offsetWidth / 2 - track.scrollLeft) - mid;
        var d = Math.abs(off) / (el.offsetWidth + g);
        if (d < bestD) { bestD = d; best = i; }
        var t = Math.min(d, 1);
        var side = off > 0 ? 1 : -1;
        el.style.transform =
          'rotateY(' + (-side * t * tilt).toFixed(2) + 'deg) scale(' + (1 - t * 0.14).toFixed(4) + ')';
        el.style.opacity = (1 - t * 0.45).toFixed(3);
        el.style.zIndex = String(100 - Math.round(d * 10));
      }
      if (best !== active) setActive(best);
    }

    function queue() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    function setAmbient(src) {
      if (amb.length !== 2 || !src) return;
      var tok = ++ambTok;
      var next = amb[1 - ambIdx];
      next.onload = function () {
        if (tok !== ambTok) return;           /* a newer card took over */
        next.classList.add('on');
        amb[ambIdx].classList.remove('on');
        ambIdx = 1 - ambIdx;
      };
      next.src = src;
    }

    function setActive(i) {
      if (items[active]) items[active].classList.remove('is-active', 'playing');
      active = i;
      var el = items[i];
      el.classList.add('is-active');
      if (auto) {
        void el.offsetWidth;                   /* restart the progress bar */
        el.classList.add('playing');
      }
      if (counter) counter.textContent = (i + 1) + ' / ' + items.length;
      if (prevBtn) prevBtn.disabled = !auto && i === 0;
      if (nextBtn) nextBtn.disabled = !auto && i === items.length - 1;
      setAmbient(el.dataset.amb);
    }

    function go(i, instant) {
      var n = items.length;
      i = auto ? (i + n) % n : Math.max(0, Math.min(n - 1, i));
      var el = items[i];
      var left = el.offsetLeft + el.offsetWidth / 2 - track.clientWidth / 2;
      track.scrollTo({ left: left, behavior: (instant || REDUCED) ? 'auto' : 'smooth' });
    }

    track.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', function () {
      go(active < 0 ? 0 : active, true);
      queue();
    }, { passive: true });

    /* a side card comes to the centre on tap; the centred card's links work */
    items.forEach(function (el, i) {
      el.addEventListener('click', function (e) {
        if (i !== active) { e.preventDefault(); go(i); }
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { go(active - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(active + 1); });

    track.addEventListener('keydown', function (e) {
      var rtl = document.documentElement.dir === 'rtl';
      if (e.key === 'ArrowRight') { e.preventDefault(); go(active + (rtl ? -1 : 1)); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); go(active + (rtl ? 1 : -1)); }
    });

    /* ---- story autoplay: the progress bar's own animation drives it ----
       Pausing the bar (hover, touch, off screen, hidden tab) therefore
       pauses the reel too, with no separate timer to keep in sync. */
    if (auto) {
      track.addEventListener('animationend', function (e) {
        if (!e.target.matches || !e.target.matches('[data-g-bar] i')) return;
        if (!items[active] || !items[active].contains(e.target)) return;
        go(active + 1);
      });

      root.addEventListener('mouseenter', function () { hold.hover = true; syncPause(); });
      root.addEventListener('mouseleave', function () { hold.hover = false; syncPause(); });

      track.addEventListener('touchstart', function () {
        clearTimeout(touchTimer);
        hold.touch = true; syncPause();
      }, { passive: true });
      track.addEventListener('touchend', function () {
        clearTimeout(touchTimer);
        touchTimer = setTimeout(function () { hold.touch = false; syncPause(); }, 2500);
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { hold.offscreen = !en.isIntersecting; syncPause(); });
        }, { threshold: 0.25 }).observe(root);
      }
      document.addEventListener('visibilitychange', syncPause);
      syncPause();
    }

    requestAnimationFrame(function () {
      go(items.length > 2 ? 1 : 0, true);
      update();
    });
    window.addEventListener('load', function () { go(active < 0 ? 0 : active, true); update(); });
  }

  function boot() {
    document.querySelectorAll('[data-gallery-flow]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
